import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, ArrowLeft, Star, MessageSquare, CheckCircle, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const specialtyColors = {
  'Film Directing': 'bg-violet-500/20 text-violet-300',
  'Fashion Design': 'bg-pink-500/20 text-pink-300',
  'Content Creation': 'bg-cyan-500/20 text-cyan-300'
};

export default function MentorCircle() {
  const { user } = useAuth();
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(null);

  useEffect(() => {
    fetchMentors();
  }, []);

  const fetchMentors = async () => {
    try {
      const res = await fetch(`${API}/api/studio/mentors`);
      if (res.ok) {
        const data = await res.json();
        setMentors(data.mentors || []);
      }
    } catch (err) {
      console.error('Error fetching mentors:', err);
    } finally {
      setLoading(false);
    }
  };

  const requestMentor = async (mentorId) => {
    if (!user) {
      toast.error('Please log in to request a mentor');
      return;
    }

    setRequesting(mentorId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/studio/mentors/request/${mentorId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        const data = await res.json();
        if (data.status === 'already_requested') {
          toast.info(`You've already requested ${data.mentor}`);
        } else {
          toast.success(data.message);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to request mentor');
      }
    } catch (err) {
      toast.error('Failed to request mentor');
    } finally {
      setRequesting(null);
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-900 to-violet-950" data-testid="mentor-circle">
      {/* Header */}
      <div className="border-b border-violet-500/30 py-16">
        <div className="container mx-auto px-4">
          <Link to="/legacystudio" className="inline-flex items-center text-violet-300 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to LegacyStudio
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-4 bg-violet-500/20 rounded-xl">
              <Users className="h-10 w-10 text-violet-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white">Mentorship Circle</h1>
              <p className="text-violet-200">Connect with industry professionals who've walked the path</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Benefits Section */}
        <div className="mb-12">
          <Card className="bg-gradient-to-br from-violet-600/20 to-purple-600/20 border-violet-400/30">
            <CardContent className="py-8">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="p-3 bg-violet-500/20 rounded-full w-fit mx-auto mb-3">
                    <MessageSquare className="h-6 w-6 text-violet-300" />
                  </div>
                  <h3 className="font-semibold text-white mb-1">1-on-1 Guidance</h3>
                  <p className="text-violet-200 text-sm">Regular check-ins and personalized career advice</p>
                </div>
                <div>
                  <div className="p-3 bg-violet-500/20 rounded-full w-fit mx-auto mb-3">
                    <Star className="h-6 w-6 text-violet-300" />
                  </div>
                  <h3 className="font-semibold text-white mb-1">Industry Insights</h3>
                  <p className="text-violet-200 text-sm">Learn from real-world experience in creative fields</p>
                </div>
                <div>
                  <div className="p-3 bg-violet-500/20 rounded-full w-fit mx-auto mb-3">
                    <Users className="h-6 w-6 text-violet-300" />
                  </div>
                  <h3 className="font-semibold text-white mb-1">Network Access</h3>
                  <p className="text-violet-200 text-sm">Get introductions to industry connections</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mentors Grid */}
        <h2 className="text-2xl font-bold text-white mb-6">Available Mentors</h2>
        
        {mentors.length === 0 && !loading ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-violet-400 mx-auto mb-4" />
              <p className="text-violet-200">No mentors currently accepting new mentees. Check back soon!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mentors.map((mentor, idx) => {
              const spotsLeft = mentor.mentees_max - mentor.mentees_current;
              const colorClass = specialtyColors[mentor.specialty] || 'bg-gray-500/20 text-gray-300';

              return (
                <motion.div
                  key={mentor.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className="h-full bg-white/5 backdrop-blur border-white/10 hover:border-violet-400/50 transition-all">
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <Avatar className="h-16 w-16 bg-gradient-to-br from-violet-500 to-purple-600">
                          <AvatarFallback className="text-white text-lg font-semibold">
                            {getInitials(mentor.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <CardTitle className="text-white text-lg">{mentor.name}</CardTitle>
                          <Badge className={colorClass}>{mentor.specialty}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-violet-200 text-sm mb-4">{mentor.bio}</p>
                      
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center text-sm text-violet-300">
                          <Users className="h-4 w-4 mr-1" />
                          {mentor.mentees_current}/{mentor.mentees_max} mentees
                        </div>
                        {spotsLeft > 0 ? (
                          <Badge className="bg-emerald-500/20 text-emerald-300">
                            {spotsLeft} spot{spotsLeft > 1 ? 's' : ''} left
                          </Badge>
                        ) : (
                          <Badge className="bg-stone-600 text-stone-300">
                            Full
                          </Badge>
                        )}
                      </div>

                      <Button
                        onClick={() => requestMentor(mentor.id)}
                        disabled={requesting === mentor.id || spotsLeft === 0 || !user}
                        className="w-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                      >
                        {requesting === mentor.id ? (
                          'Requesting...'
                        ) : spotsLeft === 0 ? (
                          'Currently Full'
                        ) : !user ? (
                          'Log in to Request'
                        ) : (
                          <>
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Request Mentorship
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* How It Works */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-white mb-6">How Mentorship Works</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: 1, title: 'Request', desc: 'Choose a mentor and submit your request' },
              { step: 2, title: 'Review', desc: 'Mentor reviews your profile and portfolio' },
              { step: 3, title: 'Connect', desc: "If it's a match, you'll be paired together" },
              { step: 4, title: 'Grow', desc: 'Regular sessions to develop your career' }
            ].map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full bg-white/5 border-white/10 text-center">
                  <CardContent className="py-6">
                    <div className="w-10 h-10 rounded-full bg-violet-600 text-white font-bold flex items-center justify-center mx-auto mb-3">
                      {item.step}
                    </div>
                    <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-violet-300 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
